/**
 * Reusable dialogs for the Procurement Management screen.
 *
 * Each form supports three modes via the `mode` prop:
 *   - 'create'  → empty form, POST on save
 *   - 'edit'    → pre-filled form, PATCH/PUT on save
 *   - 'view'    → read-only display, no save button
 *
 * The parent (ProcurementManagement.js) opens these dialogs with the
 * appropriate initial data and refetches its lists on successful save.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AttachmentsPicker from './AttachmentsPicker';

const API = `${BACKEND_URL}/api`;

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const titleFor = (mode, kind) => {
  const action = mode === 'create' ? 'New' : mode === 'edit' ? 'Edit' : 'View';
  return `${action} ${kind}`;
};

const renderField = (mode, label, name, value, onChange, opts = {}) => {
  const isView = mode === 'view';
  const { type = 'text', textarea, placeholder, options, required } = opts;
  return (
    <div className="space-y-1" key={name}>
      <Label htmlFor={name} className="text-xs text-gray-600">
        {label}{required && !isView ? ' *' : ''}
      </Label>
      {options ? (
        <select
          id={name}
          name={name}
          disabled={isView}
          value={value || ''}
          onChange={(e) => onChange(name, e.target.value)}
          className="w-full border rounded-md px-2 py-1.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-600"
          data-testid={`field-${name}`}
        >
          <option value="">{placeholder || 'Select…'}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : textarea ? (
        <Textarea
          id={name}
          rows={3}
          disabled={isView}
          value={value || ''}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={placeholder}
          data-testid={`field-${name}`}
        />
      ) : (
        <Input
          id={name}
          type={type}
          disabled={isView}
          value={value ?? ''}
          onChange={(e) => onChange(name, type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
          placeholder={placeholder}
          data-testid={`field-${name}`}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Vendor Dialog
// ────────────────────────────────────────────────────────────────────
export const VendorDialog = ({ open, onClose, mode, projectId, vendor, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm(vendor || { status: 'active' });
  }, [vendor, open]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'create') {
        await axios.post(`${API}/projects/${projectId}/vendors`, form, { headers: headers() });
        toast.success('Vendor added');
      } else {
        await axios.patch(`${API}/projects/${projectId}/vendors/${vendor.id}`, form, { headers: headers() });
        toast.success('Vendor updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vendor-dialog">
        <DialogHeader><DialogTitle>{titleFor(mode, 'Vendor')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {renderField(mode, 'Name', 'name', form.name, change, { required: true })}
          {renderField(mode, 'Category', 'category', form.category, change, {
            options: [
              { value: 'consulting', label: 'Consulting' },
              { value: 'construction', label: 'Construction' },
              { value: 'equipment', label: 'Equipment' },
              { value: 'services', label: 'Services' },
              { value: 'supplies', label: 'Supplies' },
              { value: 'other', label: 'Other' },
            ],
          })}
          {renderField(mode, 'Email', 'email', form.email, change, { type: 'email' })}
          {renderField(mode, 'Phone', 'phone', form.phone, change)}
          {renderField(mode, 'Specialization', 'specialization', form.specialization, change)}
          {renderField(mode, 'Rating (1–5)', 'rating', form.rating, change, { type: 'number' })}
          <div className="col-span-2">
            {renderField(mode, 'Address', 'address', form.address, change, { textarea: true })}
          </div>
          {renderField(mode, 'Status', 'status', form.status, change, {
            options: [
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'pending', label: 'Pending' },
            ],
          })}
          {renderField(mode, 'Tax ID / VAT', 'tax_id', form.tax_id, change)}
          <AttachmentsPicker
            mode={mode}
            projectId={projectId}
            value={form.attachments}
            onChange={(next) => change('attachments', next)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{mode === 'view' ? 'Close' : 'Cancel'}</Button>
          {mode !== 'view' && (
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="vendor-save-btn">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {mode === 'create' ? 'Add Vendor' : 'Save changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────
// Purchase Order Dialog
// ────────────────────────────────────────────────────────────────────
export const PurchaseOrderDialog = ({ open, onClose, mode, projectId, order, vendors, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [budgetCategories, setBudgetCategories] = useState([]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm(order || { status: 'draft', items: [] });
  }, [order, open]);

  // Load budget categories once when the dialog opens so the user can link
  // the PO to a specific budget line — required by the financial-integrity
  // rules (a PO is a commitment against budget).
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/projects/${projectId}/budget-categories`,
          { headers: headers() }
        );
        if (!cancelled) setBudgetCategories(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setBudgetCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.order_number) { toast.error('Order number is required'); return; }
    if (!form.vendor_name && !form.vendor_id) { toast.error('Vendor is required'); return; }
    if (!form.budget_category_id) {
      toast.error('A budget category is required for every purchase order');
      return;
    }
    setSaving(true);
    try {
      // If selecting from vendors dropdown, fill vendor_name
      const payload = { ...form };
      if (payload.vendor_id && !payload.vendor_name) {
        const v = vendors?.find((x) => x.id === payload.vendor_id);
        if (v) payload.vendor_name = v.name;
      }
      if (payload.budget_category_id) {
        const c = budgetCategories.find((b) => b.id === payload.budget_category_id);
        payload.budget_category_name = c?.name || c?.category || '';
      }
      if (mode === 'create') {
        await axios.post(`${API}/projects/${projectId}/purchase-orders`, payload, { headers: headers() });
        toast.success('Purchase order created');
      } else {
        await axios.patch(`${API}/projects/${projectId}/purchase-orders/${order.id}`, payload, { headers: headers() });
        toast.success('Purchase order updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const vendorOptions = (vendors || []).map((v) => ({ value: v.id, label: v.name }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="po-dialog">
        <DialogHeader><DialogTitle>{titleFor(mode, 'Purchase Order')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          {renderField(mode, 'Order Number', 'order_number', form.order_number, change, { required: true, placeholder: 'PO-2026-001' })}
          {renderField(mode, 'Vendor', 'vendor_id', form.vendor_id, change, { options: vendorOptions, required: true })}
          <div className="col-span-2">
            {renderField(mode, 'Description', 'description', form.description, change, { textarea: true })}
          </div>
          {renderField(mode, 'Total Amount (€)', 'total_amount', form.total_amount, change, { type: 'number' })}
          {renderField(mode, 'Budget Category', 'budget_category_id', form.budget_category_id, change, {
            required: true,
            options: budgetCategories.map((c) => ({
              value: c.id,
              label: `${c.name || c.category || 'Unnamed'} · €${Number(c.allocated_amount || 0).toLocaleString('el-GR')}`,
            })),
          })}
          {renderField(mode, 'Delivery Date', 'delivery_date', form.delivery_date?.slice(0, 10), change, { type: 'date' })}
          {renderField(mode, 'Status', 'status', form.status, change, {
            options: [
              { value: 'draft', label: 'Draft' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'received', label: 'Received' },
              { value: 'rejected', label: 'Rejected' },
            ],
          })}
          {renderField(mode, 'Currency', 'currency', form.currency || 'EUR', change)}
          <div className="col-span-2">
            {renderField(mode, 'Notes', 'notes', form.notes, change, { textarea: true })}
          </div>
          <AttachmentsPicker
            mode={mode}
            projectId={projectId}
            value={form.attachments}
            onChange={(next) => change('attachments', next)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{mode === 'view' ? 'Close' : 'Cancel'}</Button>
          {mode !== 'view' && (
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="po-save-btn">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {mode === 'create' ? 'Create Order' : 'Save changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────
// Contract Dialog
// ────────────────────────────────────────────────────────────────────
export const ContractDialog = ({ open, onClose, mode, projectId, contract, vendors, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm(contract || { status: 'draft' });
  }, [contract, open]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    if (!form.contract_number) { toast.error('Contract number is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.vendor_id && !payload.vendor_name) {
        const v = vendors?.find((x) => x.id === payload.vendor_id);
        if (v) payload.vendor_name = v.name;
      }
      if (mode === 'create') {
        await axios.post(`${API}/projects/${projectId}/contracts`, payload, { headers: headers() });
        toast.success('Contract created');
      } else {
        await axios.put(`${API}/projects/${projectId}/contracts/${contract.id}`, payload, { headers: headers() });
        toast.success('Contract updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const vendorOptions = (vendors || []).map((v) => ({ value: v.id, label: v.name }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="contract-dialog">
        <DialogHeader><DialogTitle>{titleFor(mode, 'Contract')}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            {renderField(mode, 'Title', 'title', form.title, change, { required: true })}
          </div>
          {renderField(mode, 'Contract Number', 'contract_number', form.contract_number, change, { required: true, placeholder: 'C-2026-001' })}
          {renderField(mode, 'Vendor', 'vendor_id', form.vendor_id, change, { options: vendorOptions })}
          {renderField(mode, 'Contract Value (€)', 'contract_value', form.contract_value, change, { type: 'number' })}
          {renderField(mode, 'Status', 'status', form.status, change, {
            options: [
              { value: 'draft', label: 'Draft' },
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed' },
              { value: 'terminated', label: 'Terminated' },
              { value: 'expired', label: 'Expired' },
            ],
          })}
          {renderField(mode, 'Start Date', 'start_date', form.start_date?.slice(0, 10), change, { type: 'date' })}
          {renderField(mode, 'End Date', 'end_date', form.end_date?.slice(0, 10), change, { type: 'date' })}
          <div className="col-span-2">
            {renderField(mode, 'Description', 'description', form.description, change, { textarea: true })}
          </div>
          <div className="col-span-2">
            {renderField(mode, 'Terms & Conditions', 'terms', form.terms, change, { textarea: true })}
          </div>
          <AttachmentsPicker
            mode={mode}
            projectId={projectId}
            value={form.attachments}
            onChange={(next) => change('attachments', next)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{mode === 'view' ? 'Close' : 'Cancel'}</Button>
          {mode !== 'view' && (
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="contract-save-btn">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {mode === 'create' ? 'Create Contract' : 'Save changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
